// utils/telefonoErrorApi.ts — mostrar junto al campo el rechazo del servidor a un teléfono (round 2026-10-30).
//
// El servidor rechaza un teléfono con dos códigos:
//   · 409 TELEFONO_DUPLICADO — ya lo usa otra persona del equipo.
//   · 400 TELEFONO_INVALIDO  — el formato no cumple (falta el código de país, letras, largo…).
// `apiFetch` ya muestra el mensaje como aviso global de unos segundos; esto agrega lo que faltaba: marcar el campo del teléfono
// con el mismo mensaje, llevar la pantalla hasta él y dejar el cursor ahí, para que la persona vea QUÉ campo corregir.
import type { FormInstance } from "antd";
import type { ErrorApi } from "../services/apiClient";
import { llevarCampoALaVista } from "./formularioErrores";

export const CODIGOS_ERROR_TELEFONO = ["TELEFONO_DUPLICADO", "TELEFONO_INVALIDO"] as const;

/** ¿El servidor rechazó la llamada por el teléfono? */
export const esErrorDeTelefono = (error: Pick<ErrorApi, "code"> | null | undefined): boolean =>
  !!error?.code && (CODIGOS_ERROR_TELEFONO as readonly string[]).includes(error.code);

/**
 * Si el error es del teléfono, deja el mensaje en el estado de error del campo `campo` del formulario (se borra solo cuando la
 * persona vuelve a editarlo), se desplaza hasta él y le da el foco. Devuelve `true` si lo marcó.
 */
export function marcarCampoTelefono(form: FormInstance, campo: string, error: ErrorApi | null | undefined): boolean {
  if (!error || !esErrorDeTelefono(error)) return false;
  form.setFields([{ name: campo, errors: [error.message] }]);
  // El foco y el desplazamiento son una ayuda: si el campo no está montado o el entorno no los soporta, el error ya quedó marcado.
  llevarCampoALaVista(form, campo);
  return true;
}

/**
 * Lo mismo cuando el formulario manda VARIOS teléfonos a la vez y el servidor no dice cuál rechazó (ronda F3b). Con un solo
 * candidato no hay duda y es exactamente `marcarCampoTelefono`. Con varios NO se adivina —marcar solo el primero dejaba el motivo bajo
 * un número correcto mientras el malo quedaba sin señal—: se marcan todos los candidatos con el motivo del servidor y una frase que
 * dice que no se sabe cuál es, y el foco va al primero. Devuelve `true` si lo marcó.
 */
export function marcarCamposTelefono(form: FormInstance, campos: readonly string[], error: ErrorApi | null | undefined): boolean {
  if (!error || !esErrorDeTelefono(error) || campos.length === 0) return false;
  if (campos.length === 1) return marcarCampoTelefono(form, campos[0], error);
  const texto = error.message.trim();
  const motivo = /[.!?]$/.test(texto) ? texto : `${texto}.`;
  const mensaje = `${motivo} El servidor no dijo cuál de los teléfonos es: revisa ${campos.length === 2 ? "los dos" : "todos"}.`;
  form.setFields(campos.map((name) => ({ name, errors: [mensaje] })));
  llevarCampoALaVista(form, campos[0]);
  return true;
}
